// Thin wrappers around Capacitor native APIs.
// On the web they fall back to browser APIs so the same code
// works in dev + production web builds.

import { Capacitor } from "@capacitor/core";
import { Camera, CameraResultType, CameraSource } from "@capacitor/camera";
import { Geolocation } from "@capacitor/geolocation";

export const isNative = () => Capacitor.isNativePlatform();

/**
 * Apply a platform class to <html> so we can target the Capacitor Android
 * WebView with extra safe-area padding.
 *
 * v79.61 — REMOVED @capacitor/status-bar. Google Play Console flagged the
 * plugin for using deprecated Android 15 APIs (Window.setStatusBarColor,
 * Window.setNavigationBarColor, Window.getStatusBarColor). Those APIs are
 * called internally by `StatusBar.setBackgroundColor()` /
 * `StatusBar.setOverlaysWebView()` — even though our target SDK is 35 and
 * the plugin is a no-op at runtime, the *class references* are still baked
 * into the AAB and Play Console scans for them.
 *
 * Replacement: `androidx.activity.EdgeToEdge.enable(this)` in
 * MainActivity.java (already wired up) handles Android 15 edge-to-edge via
 * the modern WindowInsetsControllerCompat / WindowCompat APIs. The
 * transparent status + nav bar, the light-icons-on-dark styling, and the
 * WebView overlay are all delivered without touching a single deprecated
 * method. The CSS rule
 *   html.is-capacitor.is-android body { padding-top: max(env(safe-area-inset-top), 40px); }
 * still keeps React content clear of the transparent status bar.
 */
export function installSafeAreaShim() {
    try {
        const root = document.documentElement;
        const ua = (navigator.userAgent || "") + " " + (navigator.platform || "");
        const isAdukooApp = /AdukooApp\//.test(ua);
        const isNative = Capacitor.isNativePlatform() || isAdukooApp;
        if (isNative) {
            let platform = "android";
            try { platform = Capacitor.getPlatform() || (/iPad|iPhone|iPod/.test(ua) ? "ios" : "android"); } catch { /* noop */ }
            if (platform !== "android" && platform !== "ios") platform = /iPad|iPhone|iPod/.test(ua) ? "ios" : "android";
            root.classList.add("is-capacitor", `is-${platform}`);
            // No plugin calls needed — EdgeToEdge + CSS handle everything.
        }
    } catch {
        // no-op: web build, no Capacitor
    }
}

/**
 * Take one photo using the native camera (on iOS/Android) or
 * fall back to a browser file-picker on web. Returns a JPEG data URL.
 */
export async function capturePhoto() {
    if (isNative()) {
        const photo = await Camera.getPhoto({
            quality: 75,
            resultType: CameraResultType.DataUrl,
            source: CameraSource.Prompt,
            width: 1200,
            height: 1200,
            correctOrientation: true,
            promptLabelHeader: "Add product photo",
            promptLabelCancel: "Cancel",
            promptLabelPhoto: "From gallery",
            promptLabelPicture: "Take photo",
        });
        return photo.dataUrl || null;
    }
    return new Promise((resolve) => {
        const input = document.createElement("input");
        input.type = "file";
        input.accept = "image/*";
        input.capture = "environment";
        input.onchange = () => {
            const file = input.files && input.files[0];
            if (!file) { resolve(null); return; }
            const reader = new FileReader();
            reader.onload = () => resolve(reader.result);
            reader.onerror = () => resolve(null);
            reader.readAsDataURL(file);
        };
        input.click();
    });
}

/** Request device GPS location. Returns { lat, lng } or null on denial. */
export async function getGpsLocation() {
    try {
        if (isNative()) {
            const perm = await Geolocation.requestPermissions();
            if (perm.location !== "granted") return null;
        }
        const pos = await Geolocation.getCurrentPosition({ enableHighAccuracy: true, timeout: 10000 });
        return { lat: pos.coords.latitude, lng: pos.coords.longitude };
    } catch {
        return null;
    }
}

/** Reverse-geocode (free, no key) using OpenStreetMap Nominatim. Returns state/city/pincode. */
export async function reverseGeocodeIndia(lat, lng) {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&countrycodes=in`,
            { headers: { "Accept-Language": "en" } }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const a = data.address || {};
        return {
            state: a.state,
            city: a.city || a.town || a.village || a.county,
            pincode: a.postcode,
        };
    } catch {
        return null;
    }
}

/** Global reverse-geocode (any country). Returns address + city + state + country + pincode + display_name. */
export async function reverseGeocodeGlobal(lat, lng) {
    try {
        const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1&zoom=18`,
            { headers: { "Accept-Language": "en" } }
        );
        if (!res.ok) return null;
        const data = await res.json();
        const a = data.address || {};
        const street = [a.house_number, a.road].filter(Boolean).join(" ");
        return {
            address: [street, a.suburb, a.neighbourhood].filter(Boolean).join(", "),
            city: a.city || a.town || a.village || a.county || "",
            state: a.state || a.region || "",
            country: (a.country_code || "").toUpperCase(),
            pincode: a.postcode || "",
            display_name: data.display_name || "",
        };
    } catch {
        return null;
    }
}
